import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { EMPTY, forkJoin, of, Subject } from 'rxjs';
import { comIdempotencia, novaChaveIdempotencia } from 'src/interceptors/idempotencia-interceptor';

import { PrestadorDiretoriaCargoRequest, PrestadorDiretoriaCargoResponse, PrestadorDiretoriaHistoricoRemocaoRequest, PrestadorDiretoriaRequest, PrestadorDiretoriaResponse } from '@/core/api/sicom-api/registro/prestador/prestador-diretoria.model';
import { DebitosService } from '@/core/api/sicom-api/financeiro/arrecadacao/debitos/debitos.service';
import { PrestadorDiretoriaService } from '@/core/api/sicom-api/registro/prestador/prestador-diretoria.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { FotoDiretor } from '@/shared/components/foto-diretor/foto-diretor';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { FormDatePicker } from '@/shared/components/layout/form-date-picker/form-date-picker';
import { DateUtils } from '@/shared/utils/date-utils';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { DIRETORIA_FINANCEIRO_RETURN_CONTEXT_ID, PrestadorFinanceiroNavigationService } from '@/features/registro/prestadores/dashboard-prestadores/components/prestador-financeiro-navigation.service';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { SeletorMedicoCorpoClinico } from '@/shared/components/pessoa/seletor-medico-corpo-clinico/seletor-medico-corpo-clinico';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { TableModule } from 'primeng/table';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type HistoricoDiretoriaLinha = {
    idMedico: number;
    uf: string;
    crm: string;
    nome: string;
    cargo: string;
    tipoDiretor: string;
    dtInicial: string;
    dtFinal: string;
    entrada: string;
    saida: string;
};

type DiretorFuturoLinha = {
    idMedico: number | null;
    tipoDiretor: string;
    uf: string;
    crm: string;
    nome: string;
    cargo: string;
    entrada: string;
    saida: string;
};

/** Mandato oferecido na seleção de exclusão (legado: grid do `frmSelecionaDiretor`). */
type MandatoVigenteLinha = {
    idMedico: number;
    uf: string;
    crm: string;
    nome: string;
    cargo: string;
    tipoDiretor: string;
    dtInicial: string;
    entrada: string;
    saida: string;
};

type CargoDiretoriaKey = 'diretorTecnico' | 'diretorTecnicoSubstituto' | 'diretorClinico' | 'viceDiretorClinico';

const CARGOS_DIRETORIA: CargoDiretoriaKey[] = ['diretorTecnico', 'diretorTecnicoSubstituto', 'diretorClinico', 'viceDiretorClinico'];

type CargoDiretoriaConfig = {
    crm: string;
    uf: string;
    nome: string;
    entrada: string;
    saida: string;
    tipoDiretor: string;
};

type ModoPesquisaDiretor = 'selecao' | 'substituicao';
type FonteSelecaoDiretor = 'generico' | 'corpoClinico';

type ConflitoDiretorFuturo = {
    cargo: CargoDiretoriaKey;
    cargoLabel: string;
    atual: PrestadorDiretoriaCargoResponse;
    futuro: PrestadorDiretoriaCargoResponse;
    alteracao: PrestadorDiretoriaCargoRequest;
    alteracaoDiretor: PrestadorDiretoriaCargoResponse | PrestadorDiretoriaCargoRequest;
};

type DiretorComDebitos = {
    id: number;
    uf: string;
    crm: string;
    nome: string;
    cargo: string;
    totalDebitos: number;
};

@Component({
    selector: 'app-diretoria-prestador',
    standalone: true,
    imports: [DialogoPadraoDirective, NgTemplateOutlet, ReactiveFormsModule, CardContent, Button, InputText, TableModule, ListboxModule, FormDatePicker, BotaoAcoes, Dialog, Pesquisa, SeletorMedicoCorpoClinico, FotoDiretor],
    templateUrl: './diretoria-prestador.html',
    styleUrl: './diretoria-prestador.scss'
})
export class DiretoriaPrestador {
    pessoa = input<Pessoa>();
    protected readonly rolePrestadoresDiretoriaEditar = PERMISSOES.prestadores.diretoria.editar;
    protected readonly rolePrestadoresDiretoriaExcluir = PERMISSOES.prestadores.diretoria.excluir;
    protected readonly EInconPos = EInconPos;

    get itensAcoes(): AppMenuItem[] {
        const role = this.rolePrestadoresDiretoriaEditar;
        const editando = this.isEditing;

        // Dois itens por cargo: busca generica (qualquer medico) e atalho escopado ao corpo clinico.
        // A populacao da diretoria nao e restrita; o atalho e apenas conveniencia.
        const acoesCargo = this.cargosDiretoria().flatMap((cargo) => {
            const substituindo = this.temDiretor(cargo);
            const desabilitado = substituindo ? !this.podeSubstituir(cargo) : !this.podeSelecionar(cargo);
            const verbo = substituindo ? 'Substituir' : 'Selecionar';
            const rotulo = this.cargoLabel(cargo).toLowerCase();
            return [
                acaoMenuItem({
                    id: cargo,
                    label: `${verbo} ${rotulo}`,
                    icon: substituindo ? 'pi pi-user-edit' : 'pi pi-user-plus',
                    cor: 'neutro',
                    disabled: desabilitado,
                    command: () => this.acionarPesquisaDiretor(cargo)
                }),
                acaoMenuItem({
                    id: `${cargo}-corpo-clinico`,
                    label: `${verbo} ${rotulo} (corpo clínico)`,
                    icon: 'pi pi-users',
                    cor: 'neutro',
                    disabled: desabilitado,
                    command: () => this.acionarPesquisaDiretorCorpoClinico(cargo)
                })
            ];
        });

        return [
            acaoMenuItem({ id: 'editar', label: 'Editar', icon: 'pi pi-pencil', cor: 'editar', disabled: editando, permission: role, command: () => this.enableEditing() }),
            acaoMenuItem({ id: 'debitos', label: 'Débitos', icon: 'pi pi-wallet', cor: 'info', disabled: !this.podeConsultarDebitosDiretoria(), command: () => this.abrirDebitosDiretoria() }),
            // Exclusão apaga o vínculo em vez de encerrá-lo, então tem role própria e fica fora do
            // modo de edição — é correção de cadastro, não parte do fluxo de salvar.
            acaoMenuItem({
                id: 'excluir-diretor',
                label: 'Excluir diretor',
                icon: 'pi pi-trash',
                cor: 'remover',
                disabled: editando || this.carregando(),
                permission: this.rolePrestadoresDiretoriaExcluir,
                command: () => this.abrirSelecaoExclusaoDiretor()
            }),
            ...acoesCargo
        ];
    }

    /** Ver {@link itensAcoes}: Salvar e Cancelar saem do menu e viram ação em destaque. */
    get itensEdicao(): AppMenuItem[] {
        const role = this.rolePrestadoresDiretoriaEditar;
        return [
            acaoMenuItem({ id: 'salvar', label: 'Salvar', icon: 'pi pi-check', cor: 'salvar', disabled: !this.form.valid, permission: role, command: () => this.salvar() }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', permission: role, command: () => this.cancelar() })
        ];
    }

    private readonly destroyRef = inject(DestroyRef);
    private readonly camposEditaveis = ['dtEntrada', 'dtSaida', 'dtsEntrada', 'dtsSaida', 'dcEntrada', 'dcSaida', 'vdcEntrada', 'vdcSaida'] as const;

    private readonly fb = inject(FormBuilder);
    readonly form = this.fb.group({
        dtCrm: [{ value: '', disabled: true }],
        dtUf: [{ value: '', disabled: true }],
        dtNome: [{ value: '', disabled: true }],
        dtEntrada: this.fb.control<Date | null>({ value: null, disabled: true }, Validators.required),
        dtSaida: this.fb.control<Date | null>({ value: null, disabled: true }),
        dtsCrm: [{ value: '', disabled: true }],
        dtsUf: [{ value: '', disabled: true }],
        dtsNome: [{ value: '', disabled: true }],
        dtsEntrada: this.fb.control<Date | null>({ value: null, disabled: true }),
        dtsSaida: this.fb.control<Date | null>({ value: null, disabled: true }),
        dcCrm: [{ value: '', disabled: true }],
        dcUf: [{ value: '', disabled: true }],
        dcNome: [{ value: '', disabled: true }],
        dcEntrada: this.fb.control<Date | null>({ value: null, disabled: true }),
        dcSaida: this.fb.control<Date | null>({ value: null, disabled: true }),
        vdcCrm: [{ value: '', disabled: true }],
        vdcUf: [{ value: '', disabled: true }],
        vdcNome: [{ value: '', disabled: true }],
        vdcEntrada: this.fb.control<Date | null>({ value: null, disabled: true }),
        vdcSaida: this.fb.control<Date | null>({ value: null, disabled: true })
    });
    readonly carregando = signal(false);
    readonly carregandoDebitos = signal(false);
    readonly dataSubstituicaoControl = new FormControl<Date | null>(null, Validators.required);

    historicoDiretoria: HistoricoDiretoriaLinha[] = [];
    diretoresFuturos: DiretorFuturoLinha[] = [];
    diretoresComDebitos: DiretorComDebitos[] = [];
    isEditing = false;
    /**
     * Rascunho da edição em andamento. O seletor de médico escreve aqui, então a partir da
     * primeira escolha ele já diverge do que o servidor tem gravado.
     */
    dadosCarregados: PrestadorDiretoriaResponse | null = null;
    /**
     * Último estado confirmado pelo servidor (carga inicial ou salvamento bem-sucedido). É a base
     * do rollback e a fonte das regras de "tem diretor / pode substituir": o rascunho não serve
     * para isso porque uma escolha ainda não salva já o alterou.
     */
    private snapshotServidor: PrestadorDiretoriaResponse | null = null;
    debitosDiretoriaDialog = false;
    substituicaoDataDialog = false;
    pesquisaMedicoDialog = false;
    corpoClinicoDialog = false;
    fonteSelecaoDiretor: FonteSelecaoDiretor = 'generico';
    confirmacaoDiretorFuturoDialog = false;
    confirmacaoRemocaoHistoricoDialog = false;
    historicoSelecionadoParaRemocao: HistoricoDiretoriaLinha | null = null;
    cargoSubstituicao: CargoDiretoriaKey | null = null;
    modoPesquisaDiretor: ModoPesquisaDiretor | null = null;
    requestPendenteConfirmacao: PrestadorDiretoriaRequest | null = null;
    conflitosDiretoresFuturos: ConflitoDiretorFuturo[] = [];
    confirmacaoCargoOcupadoDialog = false;
    requestPendenteCargoOcupado: PrestadorDiretoriaRequest | null = null;
    mensagemConflitoCargoOcupado = '';
    selecaoExclusaoDialog = false;
    confirmacaoExclusaoVigenteDialog = false;
    mandatosParaExclusao: MandatoVigenteLinha[] = [];
    mandatoSelecionadoParaExclusao: MandatoVigenteLinha | null = null;
    substituicoes = new Map<CargoDiretoriaKey, PrestadorDiretoriaCargoResponse>();
    selecoes = new Set<CargoDiretoriaKey>();

    /** Pedidos de recarga da diretoria. Ver a assinatura no construtor. */
    private readonly recarregar$ = new Subject<Pessoa>();
    /**
     * Chave de idempotência da sessão de edição: nasce ao entrar em edição e vale para todas as
     * tentativas de salvar dali — inclusive as que reenviam o mesmo request com `resolver*` marcado
     * após uma confirmação, que são a mesma intenção e não podem gravar duas vezes.
     */
    private chaveIdempotencia = novaChaveIdempotencia();

    constructor(
        private readonly prestadorDiretoriaService: PrestadorDiretoriaService,
        private readonly debitosService: DebitosService,
        private readonly financeiroNavigationService: PrestadorFinanceiroNavigationService,
        private readonly notificacoesService: NotificacoesService
    ) {
        // Recarga como fluxo, não como chamada direta: o `switchMap` descarta a busca anterior
        // quando chega um novo pedido, que é a semântica correta de "recarrega o estado atual" —
        // e é o que o antigo guard de reentrância tentava aproximar, ao custo de engolir em
        // silêncio qualquer recarga pedida de dentro do subscribe de outra operação.
        this.recarregar$
            .pipe(
                switchMap((pessoa) => {
                    // `set(true)` precisa ficar AQUI dentro, não num `tap` antes do switchMap: o
                    // switchMap desinscreve a busca anterior antes de rodar esta função, e o
                    // `finalize` dela zeraria o sinal logo depois, deixando a nova busca sem spinner.
                    this.carregando.set(true);
                    return this.prestadorDiretoriaService.buscar(pessoa).pipe(
                        finalize(() => this.carregando.set(false)),
                        // `catchError` tem que estar no fluxo interno: no externo, o primeiro erro
                        // encerraria a assinatura e nenhuma recarga posterior voltaria a funcionar.
                        catchError(() => {
                            this.limparFormulario();
                            return EMPTY;
                        })
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((response) => {
                this.aplicarDados(response);
                this.isEditing = false;
            });

        effect(() => {
            const pessoa = this.pessoa();
            if (!pessoa?.id || !pessoa?.uf) {
                this.limparFormulario();
                return;
            }

            untracked(() => this.carregarDados(pessoa));
        });
    }

    enableEditing(): void {
        this.chaveIdempotencia = novaChaveIdempotencia();
        this.isEditing = true;
        this.camposEditaveis.forEach((campo) => this.form.get(campo)?.enable({ emitEvent: false }));
    }

    salvar(): void {
        this.saveChanges();
    }

    saveChanges(): void {
        if (this.form.invalid || !this.pessoa() || !this.dadosCarregados) {
            this.form.markAllAsTouched();
            return;
        }

        const request = this.construirRequest();
        if (!request) {
            return;
        }

        const conflitos = this.conflitosFuturosDoRequest(request);
        if (conflitos.length) {
            this.requestPendenteConfirmacao = request;
            this.conflitosDiretoresFuturos = conflitos;
            this.confirmacaoDiretorFuturoDialog = true;
            return;
        }

        this.executarSalvar(request);
    }

    confirmarResolucaoDiretoresFuturos(): void {
        if (!this.requestPendenteConfirmacao) {
            return;
        }

        this.conflitosDiretoresFuturos.forEach((conflito) => {
            const cargo = this.requestPendenteConfirmacao?.[conflito.cargo];
            if (cargo) {
                cargo.resolverDiretoresFuturos = true;
            }
        });

        const request = this.requestPendenteConfirmacao;
        this.confirmacaoDiretorFuturoDialog = false;
        this.requestPendenteConfirmacao = null;
        this.conflitosDiretoresFuturos = [];
        this.executarSalvar(request);
    }

    cancelarResolucaoDiretoresFuturos(): void {
        this.confirmacaoDiretorFuturoDialog = false;
        this.requestPendenteConfirmacao = null;
        this.conflitosDiretoresFuturos = [];
    }

    /** Detalhe do 409 de cargo ocupado (DIR-040), ou `null` para qualquer outro erro. */
    private mensagemCargoOcupado(erro: unknown): string | null {
        const resposta = erro as { status?: number; error?: { errors?: string[] } };
        if (resposta?.status !== 409) {
            return null;
        }
        return resposta.error?.errors?.find((mensagem) => mensagem?.includes('DIR-040')) ?? null;
    }

    confirmarSubstituicaoCargoOcupado(): void {
        const request = this.requestPendenteCargoOcupado;
        if (!request) {
            return;
        }

        CARGOS_DIRETORIA.forEach((cargo) => {
            const alvo = request[cargo];
            if (alvo) {
                alvo.resolverCargoOcupado = true;
            }
        });

        this.confirmacaoCargoOcupadoDialog = false;
        this.requestPendenteCargoOcupado = null;
        this.mensagemConflitoCargoOcupado = '';
        this.executarSalvar(request);
    }

    cancelarSubstituicaoCargoOcupado(): void {
        this.confirmacaoCargoOcupadoDialog = false;
        this.requestPendenteCargoOcupado = null;
        this.mensagemConflitoCargoOcupado = '';
        this.reverterParaSnapshot();
    }

    // -----------------------------------------------------------------------------------------
    // Exclusão de mandato vigente (legado: btnDeletarDiretorNovo abre o frmSelecionaDiretor)
    // -----------------------------------------------------------------------------------------

    /**
     * Abre a seleção de qual mandato excluir. Como no legado, a lista traz todos os cargos — pode
     * haver mais de um diretor no mesmo cargo no banco, e é justamente aí que a escolha importa.
     */
    abrirSelecaoExclusaoDiretor(): void {
        const pessoa = this.pessoa();
        if (!pessoa) {
            return;
        }

        this.mandatoSelecionadoParaExclusao = null;
        this.mandatosParaExclusao = [];
        this.carregando.set(true);
        this.selecaoExclusaoDialog = true;

        this.prestadorDiretoriaService
            .listarMandatosVigentes(pessoa)
            .pipe(
                finalize(() => this.carregando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (mandatos) => (this.mandatosParaExclusao = mandatos.map((item) => this.mapearMandatoVigente(item))),
                error: () => (this.selecaoExclusaoDialog = false)
            });
    }

    selecionarMandatoParaExclusao(row: MandatoVigenteLinha): void {
        this.mandatoSelecionadoParaExclusao = row;
        this.selecaoExclusaoDialog = false;
        this.confirmacaoExclusaoVigenteDialog = true;
    }

    cancelarSelecaoExclusao(): void {
        this.selecaoExclusaoDialog = false;
        this.mandatosParaExclusao = [];
    }

    cancelarExclusaoVigente(): void {
        this.confirmacaoExclusaoVigenteDialog = false;
        this.mandatoSelecionadoParaExclusao = null;
    }

    confirmarExclusaoVigente(): void {
        const pessoa = this.pessoa();
        const mandato = this.mandatoSelecionadoParaExclusao;
        if (!pessoa || !mandato) {
            return;
        }

        this.carregando.set(true);
        this.prestadorDiretoriaService
            .removerVigente({
                id: pessoa.id,
                uf: pessoa.uf,
                idMedico: mandato.idMedico,
                ufMedico: mandato.uf,
                tipoDiretor: mandato.tipoDiretor,
                dtInicial: mandato.dtInicial
            })
            .pipe(
                finalize(() => this.carregando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.confirmacaoExclusaoVigenteDialog = false;
                    this.mandatoSelecionadoParaExclusao = null;
                    this.mandatosParaExclusao = [];
                    this.carregarDados(pessoa);
                    this.notificacoesService.notificar({
                        severity: 'success',
                        summary: 'Diretor excluído',
                        detail: `${mandato.cargo} removido da composição da diretoria.`
                    });
                },
                // Mesmo motivo do histórico: manter o modal aberto só repetiria a falha.
                error: () => this.cancelarExclusaoVigente()
            });
    }

    private mapearMandatoVigente(item: PrestadorDiretoriaCargoResponse): MandatoVigenteLinha {
        return {
            idMedico: item.idMedico ?? 0,
            uf: item.ufMedico ?? '',
            crm: this.toTexto(item.crm),
            nome: item.nome ?? '',
            cargo: this.cargoLabelPorTipo(item.tipoDiretor),
            tipoDiretor: item.tipoDiretor ?? '',
            dtInicial: item.dtInicial ?? '',
            entrada: this.formatarData(item.dtInicial),
            saida: this.formatarData(item.dtFinal)
        };
    }

    abrirConfirmacaoRemocaoHistorico(row: HistoricoDiretoriaLinha): void {
        if (!this.isEditing) {
            return;
        }

        this.historicoSelecionadoParaRemocao = row;
        this.confirmacaoRemocaoHistoricoDialog = true;
    }

    cancelarRemocaoHistorico(): void {
        this.confirmacaoRemocaoHistoricoDialog = false;
        this.historicoSelecionadoParaRemocao = null;
    }

    confirmarRemocaoHistorico(): void {
        const request = this.construirRemocaoHistoricoRequest();
        if (!request) {
            return;
        }

        this.carregando.set(true);
        this.prestadorDiretoriaService
            .removerHistorico(request)
            .pipe(
                finalize(() => this.carregando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.confirmacaoRemocaoHistoricoDialog = false;
                    this.historicoSelecionadoParaRemocao = null;
                    const pessoa = this.pessoa();
                    if (pessoa) {
                        this.carregarDados(pessoa);
                    }
                    this.notificacoesService.notificar({
                        severity: 'success',
                        summary: 'Diretor removido',
                        detail: 'Registro removido da composição antiga da diretoria.'
                    });
                },
                // Sem isso o modal fica aberto sobre a linha recusada, com um botão que vai falhar igual.
                error: () => this.cancelarRemocaoHistorico()
            });
    }

    private executarSalvar(request: PrestadorDiretoriaRequest): void {
        this.carregando.set(true);
        this.prestadorDiretoriaService
            .atualizar(request, comIdempotencia(this.chaveIdempotencia))
            .pipe(
                finalize(() => this.carregando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (response) => {
                    this.aplicarDados(response);
                    this.isEditing = false;
                    this.notificacoesService.notificar({
                        severity: 'success',
                        summary: 'Diretoria salva',
                        detail: 'Diretoria do prestador atualizada com sucesso.'
                    });
                },
                // A mensagem do erro vem do interceptor global; aqui só desfazemos o que a tela
                // mostra, senão o diretor recusado continua no card e volta no próximo salvamento.
                // Exceção: cargo ocupado (DIR-040) não é recusa — o backend pede confirmação
                // para substituir o diretor atual, então mantemos o rascunho e perguntamos.
                error: (erro) => {
                    const conflito = this.mensagemCargoOcupado(erro);
                    if (conflito) {
                        this.requestPendenteCargoOcupado = request;
                        this.mensagemConflitoCargoOcupado = conflito;
                        this.confirmacaoCargoOcupadoDialog = true;
                        return;
                    }
                    this.reverterParaSnapshot();
                }
            });
    }

    cancelEditing(): void {
        this.isEditing = false;
        this.reverterParaSnapshot();
    }

    cancelar(): void {
        this.cancelEditing();
    }

    // As regras abaixo leem o snapshot, não o rascunho: uma escolha ainda não salva não pode
    // transformar "selecionar" em "substituir" nem inventar um original para substituir.
    podeSubstituir(cargo: CargoDiretoriaKey): boolean {
        return this.isEditing && !!this.snapshotServidor?.[cargo]?.idMedico;
    }

    podeSelecionar(cargo: CargoDiretoriaKey): boolean {
        return this.isEditing && !this.snapshotServidor?.[cargo]?.idMedico && !this.temDiretorFuturo(cargo);
    }

    podeConsultarDebitosDiretoria(): boolean {
        return !this.carregando() && !this.carregandoDebitos() && this.diretoresAtuais().length > 0;
    }

    temDiretor(cargo: CargoDiretoriaKey): boolean {
        return !!this.snapshotServidor?.[cargo]?.idMedico;
    }

    formatarData(value: string | null | undefined): string {
        return DateUtils.formatDate(value ?? '');
    }

    diretorResumo(diretor: PrestadorDiretoriaCargoResponse | PrestadorDiretoriaCargoRequest | null | undefined): string {
        if (!diretor) {
            return '';
        }

        const crm = this.toTexto('crm' in diretor ? diretor.crm : diretor.idMedico);
        const uf = 'ufMedico' in diretor ? diretor.ufMedico : '';
        const nome = 'nome' in diretor ? diretor.nome : '';
        return [crm, uf, nome].filter(Boolean).join(' - ');
    }

    acionarPesquisaDiretor(cargo: CargoDiretoriaKey): void {
        this.fonteSelecaoDiretor = 'generico';
        if (this.temDiretor(cargo)) {
            this.abrirSubstituicao(cargo);
            return;
        }

        this.abrirSelecao(cargo);
    }

    acionarPesquisaDiretorCorpoClinico(cargo: CargoDiretoriaKey): void {
        this.fonteSelecaoDiretor = 'corpoClinico';
        if (this.temDiretor(cargo)) {
            this.abrirSubstituicao(cargo);
            return;
        }

        this.abrirSelecao(cargo);
    }

    private abrirPickerDiretor(): void {
        if (this.fonteSelecaoDiretor === 'corpoClinico') {
            this.corpoClinicoDialog = true;
            return;
        }
        this.pesquisaMedicoDialog = true;
    }

    abrirSubstituicao(cargo: CargoDiretoriaKey): void {
        if (!this.podeSubstituir(cargo)) {
            return;
        }

        this.cargoSubstituicao = cargo;
        this.modoPesquisaDiretor = 'substituicao';
        this.dataSubstituicaoControl.setValue(new Date());
        this.substituicaoDataDialog = true;
    }

    abrirSelecao(cargo: CargoDiretoriaKey): void {
        // `podeSelecionar` bloqueia quando ha diretor futuro; sem este aviso o clique nao faria
        // nada e o usuario ficaria sem saber o motivo.
        if (this.temDiretorFuturo(cargo)) {
            this.notificacoesService.notificar({
                severity: 'warn',
                summary: 'Diretor futuro agendado',
                detail: 'Já existe diretor futuro agendado para este cargo. Ajuste ou remova o agendamento antes de incluir outro diretor.'
            });
            return;
        }

        if (!this.podeSelecionar(cargo)) {
            return;
        }

        this.cargoSubstituicao = cargo;
        this.modoPesquisaDiretor = 'selecao';
        this.abrirPickerDiretor();
    }

    confirmarDataSubstituicao(): void {
        if (this.dataSubstituicaoControl.invalid) {
            this.dataSubstituicaoControl.markAsTouched();
            return;
        }

        this.substituicaoDataDialog = false;
        this.abrirPickerDiretor();
    }

    cancelarDataSubstituicao(): void {
        this.substituicaoDataDialog = false;
        this.cargoSubstituicao = null;
        this.modoPesquisaDiretor = null;
        this.fonteSelecaoDiretor = 'generico';
        this.dataSubstituicaoControl.reset();
    }

    abrirDebitosDiretoria(): void {
        const diretores = this.diretoresAtuais();
        this.diretoresComDebitos = [];
        this.debitosDiretoriaDialog = true;

        if (!diretores.length) {
            return;
        }

        const consultas = diretores.map((diretor) =>
            this.debitosService
                .listar({
                    id: diretor.id,
                    uf: diretor.uf,
                    situacao: 'PENDENTE',
                    page: 0,
                    size: 1
                })
                .pipe(
                    map((response) => ({
                        ...diretor,
                        totalDebitos: response.debitos?.totalElements ?? response.totalizadores?.registros ?? response.debitos?.numberOfElements ?? 0
                    })),
                    catchError(() => of({ ...diretor, totalDebitos: 0 }))
                )
        );

        this.carregandoDebitos.set(true);
        forkJoin(consultas)
            .pipe(
                finalize(() => this.carregandoDebitos.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((resultado) => {
                this.diretoresComDebitos = resultado.filter((diretor) => diretor.totalDebitos > 0);
            });
    }

    abrirArrecadacaoDiretor(diretor: DiretorComDebitos | null | undefined): void {
        if (!diretor?.id || !diretor.uf) {
            return;
        }
        this.debitosDiretoriaDialog = false;
        this.abrirArrecadacaoMedico(diretor.id, diretor.uf, diretor.crm, diretor.nome);
    }

    abrirArrecadacaoHistorico(row: HistoricoDiretoriaLinha): void {
        if (!row?.idMedico || !row.uf) {
            return;
        }
        this.abrirArrecadacaoMedico(row.idMedico, row.uf, row.crm, row.nome);
    }

    abrirArrecadacaoFuturo(row: DiretorFuturoLinha): void {
        if (!row?.idMedico || !row.uf) {
            return;
        }
        this.abrirArrecadacaoMedico(row.idMedico, row.uf, row.crm, row.nome);
    }

    private abrirArrecadacaoMedico(id: number, uf: string, crm?: string, nome?: string): void {
        this.financeiroNavigationService.abrirMedico(this.pessoa() as PessoaDomain | undefined, { id, uf, crm, nome, tipo: 'MEDICO' }, { id: DIRETORIA_FINANCEIRO_RETURN_CONTEXT_ID, returnUrl: '/prestadores/diretoria' });
    }

    selecionarMedicoSubstituto(medico: PessoaDomain): void {
        const cargo = this.cargoSubstituicao;
        const dataEntrada = this.modoPesquisaDiretor === 'substituicao' ? this.dataSubstituicaoControl.value : new Date();
        if (!cargo || !dataEntrada || !medico?.id || !medico.uf) {
            return;
        }

        const atual = this.snapshotServidor?.[cargo];
        // "Já é o diretor atual" é validado pelo backend (DIR-021) — não duplicamos aqui.
        if (this.modoPesquisaDiretor === 'substituicao') {
            // Vem do snapshot, então trocar de médico de novo antes de salvar reescreve o mesmo
            // valor: `idMedicoOriginal` continua sendo quem está gravado, não a escolha intermediária.
            if (atual) {
                this.substituicoes.set(cargo, { ...atual });
            }
        } else {
            this.selecoes.add(cargo);
        }

        this.preencherCargoSubstituto(cargo, medico, dataEntrada);
        this.pesquisaMedicoDialog = false;
        this.corpoClinicoDialog = false;
        this.fonteSelecaoDiretor = 'generico';
        this.cargoSubstituicao = null;
        this.modoPesquisaDiretor = null;
        this.dataSubstituicaoControl.reset();
    }

    /** Pede a recarga. Sem guard: quem chama de dentro de outro subscribe é atendido igual. */
    private carregarDados(pessoa: Pessoa): void {
        this.recarregar$.next(pessoa);
    }

    /** Firma o estado vindo do servidor como nova base e sai do modo de edição. */
    private aplicarDados(response: PrestadorDiretoriaResponse | null): void {
        if (!response) {
            this.limparFormulario();
            return;
        }

        this.snapshotServidor = this.clonar(response);
        this.reidratar(this.snapshotServidor);
        this.form.disable({ emitEvent: false });
    }

    /**
     * Descarta as alterações pendentes e volta ao último estado confirmado pelo servidor. Usado no
     * cancelamento e quando a API rejeita o salvamento — nos dois casos o que está na tela nunca
     * foi gravado. Mantém o modo de edição intacto: numa rejeição o usuário continua editando para
     * escolher outro médico.
     */
    private reverterParaSnapshot(): void {
        if (!this.snapshotServidor) {
            this.limparFormulario();
            return;
        }

        this.reidratar(this.snapshotServidor);
        if (this.isEditing) {
            this.camposEditaveis.forEach((campo) => this.form.get(campo)?.enable({ emitEvent: false }));
        } else {
            this.form.disable({ emitEvent: false });
        }
    }

    /** Reconstrói rascunho, formulário e tabelas a partir de uma base, sem mexer no modo de edição. */
    private reidratar(base: PrestadorDiretoriaResponse): void {
        const response = this.clonar(base);
        this.dadosCarregados = response;
        this.substituicoes.clear();
        this.selecoes.clear();
        this.form.patchValue(
            {
                dtCrm: this.toTexto(response.diretorTecnico?.crm),
                dtUf: response.diretorTecnico?.ufMedico ?? '',
                dtNome: response.diretorTecnico?.nome ?? '',
                dtEntrada: DateUtils.parseToDate(response.diretorTecnico?.dtInicial),
                dtSaida: DateUtils.parseToDate(response.diretorTecnico?.dtFinal),
                dtsCrm: this.toTexto(response.diretorTecnicoSubstituto?.crm),
                dtsUf: response.diretorTecnicoSubstituto?.ufMedico ?? '',
                dtsNome: response.diretorTecnicoSubstituto?.nome ?? '',
                dtsEntrada: DateUtils.parseToDate(response.diretorTecnicoSubstituto?.dtInicial),
                dtsSaida: DateUtils.parseToDate(response.diretorTecnicoSubstituto?.dtFinal),
                dcCrm: this.toTexto(response.diretorClinico?.crm),
                dcUf: response.diretorClinico?.ufMedico ?? '',
                dcNome: response.diretorClinico?.nome ?? '',
                dcEntrada: DateUtils.parseToDate(response.diretorClinico?.dtInicial),
                dcSaida: DateUtils.parseToDate(response.diretorClinico?.dtFinal),
                vdcCrm: this.toTexto(response.viceDiretorClinico?.crm),
                vdcUf: response.viceDiretorClinico?.ufMedico ?? '',
                vdcNome: response.viceDiretorClinico?.nome ?? '',
                vdcEntrada: DateUtils.parseToDate(response.viceDiretorClinico?.dtInicial),
                vdcSaida: DateUtils.parseToDate(response.viceDiretorClinico?.dtFinal)
            },
            { emitEvent: false }
        );

        this.historicoDiretoria = (response.historicoDiretoria ?? []).map((item) => ({
            idMedico: item.idMedico ?? 0,
            uf: item.ufMedico ?? '',
            crm: this.toTexto(item.crm),
            nome: item.nome ?? '',
            cargo: item.cargo ?? '',
            tipoDiretor: item.tipoDiretor ?? '',
            dtInicial: item.dtInicial ?? '',
            dtFinal: item.dtFinal ?? '',
            entrada: DateUtils.formatDate(item.dtInicial ?? ''),
            saida: DateUtils.formatDate(item.dtFinal ?? '')
        }));

        this.diretoresFuturos = (response.diretoresFuturos ?? []).map((item) => ({
            idMedico: item.idMedico ?? null,
            tipoDiretor: item.tipoDiretor ?? '',
            uf: item.ufMedico ?? '',
            crm: this.toTexto(item.crm),
            nome: item.nome ?? '',
            cargo: this.cargoLabelPorTipo(item.tipoDiretor),
            entrada: DateUtils.formatDate(item.dtInicial ?? ''),
            saida: DateUtils.formatDate(item.dtFinal ?? '')
        }));
    }

    /** Cópia profunda: rascunho e snapshot não podem compartilhar os objetos de cargo. */
    private clonar(response: PrestadorDiretoriaResponse): PrestadorDiretoriaResponse {
        return structuredClone(response);
    }

    private limparFormulario(): void {
        this.dadosCarregados = null;
        this.snapshotServidor = null;
        this.historicoDiretoria = [];
        this.diretoresFuturos = [];
        this.substituicoes.clear();
        this.selecoes.clear();
        this.requestPendenteConfirmacao = null;
        this.conflitosDiretoresFuturos = [];
        this.confirmacaoDiretorFuturoDialog = false;
        this.confirmacaoRemocaoHistoricoDialog = false;
        this.debitosDiretoriaDialog = false;
        this.diretoresComDebitos = [];
        this.historicoSelecionadoParaRemocao = null;
        this.form.reset(
            {
                dtCrm: '',
                dtUf: '',
                dtNome: '',
                dtEntrada: null,
                dtSaida: null,
                dtsCrm: '',
                dtsUf: '',
                dtsNome: '',
                dtsEntrada: null,
                dtsSaida: null,
                dcCrm: '',
                dcUf: '',
                dcNome: '',
                dcEntrada: null,
                dcSaida: null,
                vdcCrm: '',
                vdcUf: '',
                vdcNome: '',
                vdcEntrada: null,
                vdcSaida: null
            },
            { emitEvent: false }
        );
        this.form.disable({ emitEvent: false });
    }

    private construirRequest(): PrestadorDiretoriaRequest | null {
        const pessoa = this.pessoa();
        const dados = this.dadosCarregados;
        if (!pessoa || !dados) {
            return null;
        }

        const raw = this.form.getRawValue();
        return {
            id: pessoa.id,
            uf: pessoa.uf,
            diretorTecnico: this.somenteSeAlterado('diretorTecnico', this.mapearCargoRequest(dados.diretorTecnico, raw.dtEntrada, raw.dtSaida, 'diretorTecnico')),
            diretorTecnicoSubstituto: this.somenteSeAlterado('diretorTecnicoSubstituto', this.mapearCargoRequest(dados.diretorTecnicoSubstituto, raw.dtsEntrada, raw.dtsSaida, 'diretorTecnicoSubstituto')),
            diretorClinico: this.somenteSeAlterado('diretorClinico', this.mapearCargoRequest(dados.diretorClinico, raw.dcEntrada, raw.dcSaida, 'diretorClinico')),
            viceDiretorClinico: this.somenteSeAlterado('viceDiretorClinico', this.mapearCargoRequest(dados.viceDiretorClinico, raw.vdcEntrada, raw.vdcSaida, 'viceDiretorClinico'))
        };
    }

    /**
     * Envia apenas o cargo que mudou em relação ao que está gravado. Um cargo intocado não tem
     * por que trafegar: se o registro dele estiver inconsistente no banco, ele derrubava o
     * salvamento de qualquer outro cargo (erro DIR-052 ao editar um cargo diferente).
     */
    private somenteSeAlterado(key: CargoDiretoriaKey, request: PrestadorDiretoriaCargoRequest | null): PrestadorDiretoriaCargoRequest | null {
        if (!request) {
            return null;
        }

        const gravado = this.snapshotServidor?.[key];
        if (!gravado?.idMedico) {
            return request;
        }

        const mesmoMedico = gravado.idMedico === request.idMedico && gravado.ufMedico === request.ufMedico;
        const mesmasDatas = this.mesmoValor(gravado.dtInicial, request.dtInicial) && this.mesmoValor(gravado.dtFinal, request.dtFinal);
        return mesmoMedico && mesmasDatas ? null : request;
    }

    private mesmoValor(gravado: string | null | undefined, enviado: string | null | undefined): boolean {
        return (gravado ?? null) === (enviado ?? null);
    }

    private construirRemocaoHistoricoRequest(): PrestadorDiretoriaHistoricoRemocaoRequest | null {
        const pessoa = this.pessoa();
        const historico = this.historicoSelecionadoParaRemocao;
        if (!pessoa || !historico || !historico.idMedico || !historico.uf || !historico.tipoDiretor || !historico.dtInicial || !historico.dtFinal) {
            return null;
        }

        return {
            id: pessoa.id,
            uf: pessoa.uf,
            idMedico: historico.idMedico,
            ufMedico: historico.uf,
            tipoDiretor: historico.tipoDiretor,
            dtInicial: historico.dtInicial,
            dtFinal: historico.dtFinal
        };
    }

    private mapearCargoRequest(cargo: PrestadorDiretoriaCargoResponse | null | undefined, dtInicial: Date | null, dtFinal: Date | null, key?: CargoDiretoriaKey): PrestadorDiretoriaCargoRequest | null {
        if (!cargo?.idMedico || !cargo.ufMedico || !cargo.tipoDiretor) {
            return null;
        }

        // A operacao e deduzida do snapshot do servidor, nao dos mapas `selecoes`/`substituicoes`:
        // `reidratar()` limpa esses mapas, e o fallback antigo (`?? cargo.dtInicial`) transformava
        // uma inclusao em alteracao apontando para um registro inexistente (erro DIR-052).
        const snapshot = key ? this.snapshotServidor?.[key] : null;
        const gravado = snapshot?.idMedico ? snapshot : null;
        const trocouMedico = !!gravado && (gravado.idMedico !== cargo.idMedico || gravado.ufMedico !== cargo.ufMedico);
        const original = trocouMedico ? gravado : key ? (this.substituicoes.get(key) ?? null) : null;

        return {
            idMedico: cargo.idMedico,
            ufMedico: cargo.ufMedico,
            tipoDiretor: cargo.tipoDiretor,
            idMedicoOriginal: original?.idMedico ?? null,
            ufMedicoOriginal: original?.ufMedico ?? null,
            // Sem registro gravado é inclusão: `dtInicialOriginal` precisa ir nulo.
            dtInicialOriginal: gravado ? (original?.dtInicial ?? gravado.dtInicial ?? null) : null,
            dtInicial: DateUtils.formatDateToApi(dtInicial),
            dtFinal: DateUtils.formatDateToApi(dtFinal),
            resolverDiretoresFuturos: null
        };
    }

    private conflitosFuturosDoRequest(request: PrestadorDiretoriaRequest): ConflitoDiretorFuturo[] {
        if (!this.snapshotServidor) {
            return [];
        }

        return this.cargosDiretoria()
            .map((cargo) => this.conflitoFuturoDoCargo(cargo, request[cargo]))
            .filter((conflito): conflito is ConflitoDiretorFuturo => !!conflito);
    }

    private conflitoFuturoDoCargo(cargo: CargoDiretoriaKey, alteracao: PrestadorDiretoriaCargoRequest | null | undefined): ConflitoDiretorFuturo | null {
        if (!alteracao || !this.isAlteracaoAtual(cargo, alteracao)) {
            return null;
        }

        const futuro = this.snapshotServidor?.diretoresFuturos?.find((item) => item.tipoDiretor === alteracao.tipoDiretor);
        const atual = this.substituicoes.get(cargo) ?? this.snapshotServidor?.[cargo];
        if (!futuro || !atual) {
            return null;
        }

        return {
            cargo,
            cargoLabel: this.cargoLabel(cargo),
            atual,
            futuro,
            alteracao,
            alteracaoDiretor: this.dadosCarregados?.[cargo] ?? alteracao
        };
    }

    private isAlteracaoAtual(cargo: CargoDiretoriaKey, alteracao: PrestadorDiretoriaCargoRequest): boolean {
        if (this.substituicoes.has(cargo)) {
            return true;
        }

        const atual = this.snapshotServidor?.[cargo];
        return !!atual && (alteracao.dtInicial !== atual.dtInicial || alteracao.dtFinal !== (atual.dtFinal ?? null));
    }

    private temDiretorFuturo(cargo: CargoDiretoriaKey): boolean {
        const tipoDiretor = this.configCargo(cargo).tipoDiretor;
        return !!this.snapshotServidor?.diretoresFuturos?.some((item) => item.tipoDiretor === tipoDiretor);
    }

    private preencherCargoSubstituto(cargo: CargoDiretoriaKey, medico: PessoaDomain, dataSubstituicao: Date): void {
        const config = this.configCargo(cargo);
        const atual = this.dadosCarregados?.[cargo];
        if (!config || !this.dadosCarregados) {
            return;
        }

        const dtInicial = DateUtils.formatDateToApi(dataSubstituicao);
        const crm = this.toTexto(medico.crm);
        const uf = medico.uf ?? '';
        const nome = medico.nome ?? medico.nomeSocial ?? '';

        this.dadosCarregados[cargo] = {
            ...atual,
            idMedico: Number(medico.id),
            ufMedico: uf,
            crm: crm ? Number(crm) : null,
            nome,
            tipoDiretor: atual?.tipoDiretor ?? config.tipoDiretor,
            dtInicial,
            dtFinal: null
        };

        this.form.patchValue(
            {
                [config.crm]: crm,
                [config.uf]: uf,
                [config.nome]: nome,
                [config.entrada]: dataSubstituicao,
                [config.saida]: null
            },
            { emitEvent: false }
        );
    }

    private configCargo(cargo: CargoDiretoriaKey): CargoDiretoriaConfig {
        const configs: Record<CargoDiretoriaKey, CargoDiretoriaConfig> = {
            diretorTecnico: { crm: 'dtCrm', uf: 'dtUf', nome: 'dtNome', entrada: 'dtEntrada', saida: 'dtSaida', tipoDiretor: 'T' },
            diretorTecnicoSubstituto: { crm: 'dtsCrm', uf: 'dtsUf', nome: 'dtsNome', entrada: 'dtsEntrada', saida: 'dtsSaida', tipoDiretor: 'S' },
            diretorClinico: { crm: 'dcCrm', uf: 'dcUf', nome: 'dcNome', entrada: 'dcEntrada', saida: 'dcSaida', tipoDiretor: 'C' },
            viceDiretorClinico: { crm: 'vdcCrm', uf: 'vdcUf', nome: 'vdcNome', entrada: 'vdcEntrada', saida: 'vdcSaida', tipoDiretor: 'V' }
        };
        return configs[cargo];
    }

    private cargosDiretoria(): CargoDiretoriaKey[] {
        return ['diretorTecnico', 'diretorTecnicoSubstituto', 'diretorClinico', 'viceDiretorClinico'];
    }

    private diretoresAtuais(): DiretorComDebitos[] {
        return this.cargosDiretoria()
            .map((cargo) => this.mapearDiretorAtual(cargo))
            .filter((diretor): diretor is DiretorComDebitos => !!diretor);
    }

    private mapearDiretorAtual(cargo: CargoDiretoriaKey): DiretorComDebitos | null {
        const diretor = this.dadosCarregados?.[cargo];
        if (!diretor?.idMedico || !diretor.ufMedico) {
            return null;
        }

        return {
            id: diretor.idMedico,
            uf: diretor.ufMedico,
            crm: this.toTexto(diretor.crm),
            nome: diretor.nome ?? '',
            cargo: this.cargoLabel(cargo),
            totalDebitos: 0
        };
    }

    private cargoLabel(cargo: CargoDiretoriaKey): string {
        const labels: Record<CargoDiretoriaKey, string> = {
            diretorTecnico: 'Diretor Técnico',
            diretorTecnicoSubstituto: 'Diretor Técnico Substituto',
            diretorClinico: 'Diretor Clínico',
            viceDiretorClinico: 'Vice Diretor Clínico'
        };
        return labels[cargo];
    }

    private cargoLabelPorTipo(tipoDiretor: string | null | undefined): string {
        const labels: Record<string, string> = {
            T: 'Diretor Técnico',
            S: 'Diretor Técnico Substituto',
            C: 'Diretor Clínico',
            V: 'Vice Diretor Clínico'
        };
        return tipoDiretor ? (labels[tipoDiretor] ?? tipoDiretor) : '';
    }

    private toTexto(value: number | string | null | undefined): string {
        return value === null || value === undefined ? '' : String(value);
    }
}
